import type { AeropayBankAccount } from '@iheartjane/data-access';
import {
  useAeropayUser,
  useCreateAeropayUser,
} from '@iheartjane/data-access/react-query';
import { trackError } from '@iheartjane/tracking';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { getJwt } from '@jane/shared/auth';
import { useJaneUser, useUpdateJaneUser } from '@jane/shared/data-access';

export const SCREEN_NAMES = {
  aeropay: 'aeropay',
  aeropaySuccess: 'aeropay-success',
  bankSelection: 'bank-selection',
  failure: 'failure',
  error: 'error',
  expiration: 'expiration',
  loading: 'loading',
  mfa: 'mfa',
  moreInfo: 'more-info',
  paymentSuccess: 'payment-success',
  reviewOrder: 'review-order',
  reviewPayment: 'review-payment',
  success: 'success',
  successConfirmation: 'success-confirmation',
  signedInWelcome: 'signed-in-welcome',
};

type ScreenName = (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES];

export const useBankLinkingFlow = ({
  initialScreen,
  error,
  onSetNewPhone,
  setError,
}: {
  error: unknown;
  initialScreen?: (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES];
  onSetNewPhone: (phone: string | undefined) => void;
  setError: (err: unknown) => void;
}) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const [screen, _setScreen] = useState<ScreenName>(
    initialScreen || SCREEN_NAMES.signedInWelcome
  );
  const [loaded, setLoaded] = useState(false);
  const [needsMoreFlag, setNeedsMoreFlag] = useState(false);
  const [selectedBank, setSelectedBank] = useState<
    AeropayBankAccount | undefined
  >();
  const [pendingUserInfo, setPendingUserInfo] = useState<{
    first_name?: string;
    last_name?: string;
    phone?: string;
  } | null>(null);
  const setScreen = (newScreen: ScreenName) => {
    setQueryParams({ step: newScreen }, { replace: true });
    _setScreen(newScreen);
  };

  const {
    data: janeUser,
    isFetched: janeUserFetched,
    error: userError,
  } = useJaneUser();

  const jwt = getJwt();
  const { data: aeropayUser, error: aeropayUserError } = useAeropayUser({
    janeUserId: janeUser?.user?.id || null,
    jwt,
  });

  const { mutate: updateJaneUser } = useUpdateJaneUser();

  const { mutate: createAeropayUser } = useCreateAeropayUser({
    janeUserId: janeUser?.user?.id,
    jwt,
  });

  const handleSubmitInfo = useCallback(
    (data: any) => {
      setScreen(SCREEN_NAMES.loading);
      const { firstName: first_name, lastName: last_name, phone } = data;

      const phoneNumber = typeof phone === 'number' ? phone.toString() : phone;

      setPendingUserInfo({ first_name, last_name, phone: phoneNumber });
      onSetNewPhone(phoneNumber);

      if (!aeropayUser?.user_id) {
        createAeropayUser(
          {
            email: janeUser?.user?.email,
            first_name,
            last_name,
            phone: phoneNumber,
          },
          {
            onSuccess: () => {
              setScreen(SCREEN_NAMES.aeropay);
            },
            onError: (e) => {
              trackError('PCP: Error creating Aeropay user', { error: e });
              console.error(`Create Aeropay User Error: ${e}`);
              setError(e);
              setScreen(SCREEN_NAMES.failure);
            },
          }
        );
      } else {
        setScreen(SCREEN_NAMES.aeropay);
      }
    },
    [aeropayUser, screen]
  );

  const afterVerifySuccess = useCallback(() => {
    if (!pendingUserInfo) return;

    updateJaneUser(pendingUserInfo, {
      onSuccess: () => setPendingUserInfo(null),
      onError: () => {
        trackError('PCP: Error updating jane user');
        setError('updateJaneUser');
        setScreen(SCREEN_NAMES.failure);
      },
    });
  }, [pendingUserInfo]);

  useEffect(() => {
    // Allow testing of any step in the flow without automatically being redirected to the beginning
    if (queryParams.has('force-step')) {
      return;
    }
    if (error) {
      return;
    }
    if (janeUser?.user?.id && aeropayUser && !loaded) {
      const needsMoreInfo =
        !janeUser?.user?.first_name ||
        !janeUser?.user.last_name ||
        !janeUser?.user?.phone;
      setLoaded(true);
      setNeedsMoreFlag(needsMoreInfo);
      setScreen(SCREEN_NAMES.reviewOrder);
    }
  }, [aeropayUser, createAeropayUser, loaded, janeUser, janeUserFetched]);

  // User confirms order review screen
  const onConfirmOrder = () => {
    if (aeropayUser && !aeropayUser.user_id) {
      setScreen(SCREEN_NAMES.signedInWelcome);
    } else if (needsMoreFlag) {
      setScreen(SCREEN_NAMES.moreInfo);
    } else {
      setScreen(SCREEN_NAMES.aeropay);
    }
  };

  useEffect(() => {
    if (userError || aeropayUserError) {
      setError(userError ? 'fetchJaneUser' : aeropayUserError);
      setScreen(SCREEN_NAMES.failure);
    }
  }, [userError, aeropayUserError]);

  return {
    afterVerifySuccess,
    createAeropayUser,
    handleSubmitInfo,
    needsMoreFlag,
    onConfirmOrder,
    screen,
    setScreen,
    selectedBank,
    setSelectedBank,
  };
};
